import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { formatDate } from "@/lib/format";
import { LEAD_REJECTION_REASON_LABELS, REVISIT_OPTIONS, rejectionBucket, revisitDate, type RejectionBucket } from "@/lib/lead-rejection";
import { useReviewRejectedLead, type RejectedLead } from "@/hooks/use-rejected-leads";

const FILTERS: { value: RejectionBucket; label: string }[] = [
  { value: "due", label: "Til vurdering" },
  { value: "waiting", label: "Venter" },
  { value: "closed", label: "Avsluttet" },
];

/** Avviste leads med filter og vurdering (VIB-3032). */
export default function RejectedLeadsDialog({ open, onOpenChange, leads }: { open: boolean; onOpenChange: (o: boolean) => void; leads: RejectedLead[] }) {
  const [filter, setFilter] = useState<RejectionBucket>("due");
  const review = useReviewRejectedLead();
  const counts = useMemo(() => {
    const c: Record<RejectionBucket, number> = { due: 0, waiting: 0, closed: 0 };
    leads.forEach((l) => (c[rejectionBucket(l.revisit_on)] += 1));
    return c;
  }, [leads]);
  const shown = leads.filter((l) => rejectionBucket(l.revisit_on) === filter);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Avviste leads</DialogTitle>
          <DialogDescription>Leads som har sagt nei. Når datoen kommer, avgjør du om de skal ringes igjen.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter">
          {FILTERS.map((f) => (
            <Button
              key={f.value}
              size="sm"
              variant={filter === f.value ? "default" : "outline"}
              aria-pressed={filter === f.value}
              onClick={() => setFilter(f.value)}
            >
              {f.label} ({counts[f.value]})
            </Button>
          ))}
        </div>
        {shown.length === 0 ? (
          <p className="text-sm text-muted-foreground">Ingen leads her.</p>
        ) : (
          <ul className="divide-y divide-border">
            {shown.map((l) => (
              <li key={l.id} className="space-y-2 py-3 text-sm">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-medium">{l.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {l.revisit_on ? `Vurderes ${formatDate(l.revisit_on)}` : "Avsluttet"}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {[
                    LEAD_REJECTION_REASON_LABELS[l.rejection_reason ?? ""] ?? "Ingen årsak",
                    l.rejected_by_name && l.rejected_at ? `${l.rejected_by_name}, ${formatDate(l.rejected_at)}` : null,
                    l.list_owner_name ? `${l.list_name} (${l.list_owner_name})` : l.list_name,
                    l.municipality,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
                {l.note && <p className="whitespace-pre-line">{l.note}</p>}
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={review.isPending}
                    onClick={() => review.mutate({ leadId: l.id, action: "requeue" })}
                  >
                    Tilbake i køen
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline" disabled={review.isPending}>
                        Utsett
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {REVISIT_OPTIONS.filter((o) => o.months > 0).map((o) => (
                        <DropdownMenuItem
                          key={o.months}
                          onSelect={() => review.mutate({ leadId: l.id, action: "postpone", revisitOn: revisitDate(o.months) })}
                        >
                          {o.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {l.revisit_on && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={review.isPending}
                      onClick={() => review.mutate({ leadId: l.id, action: "close" })}
                    >
                      Avslutt for godt
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
